import { useEffect } from 'react';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Crown, Coins, Star, ArrowRight } from 'lucide-react';

interface LevelUpModalProps {
  newLevel: number;
  onClose: () => void;
}

export default function LevelUpModal({ newLevel, onClose }: LevelUpModalProps) {
  
  useEffect(() => {
    // הפעלת קונפטי ברגע שהמודל עולה
    const duration = 3000;
    const end = Date.now() + duration;

    const frame = () => {
      confetti({
        particleCount: 5,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#fbbf24', '#818cf8', '#ef4444']
      });
      confetti({
        particleCount: 5,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#fbbf24', '#818cf8', '#ef4444']
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* רקע כהה ומטושטש */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* כרטיס החגיגה */}
      <motion.div 
        initial={{ scale: 0.5, opacity: 0, y: 100 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        transition={{ type: "spring", bounce: 0.5 }}
        className="relative bg-gradient-to-br from-secondary/30 to-card w-full max-w-sm rounded-3xl p-8 text-center border-4 border-yellow-500/50 shadow-[0_0_50px_rgba(234,179,8,0.4)] overflow-hidden"
      >
        {/* קרני אור מסתובבות ברקע */}
        <div className="absolute inset-0 flex items-center justify-center opacity-30 pointer-events-none">
             <div className="w-[500px] h-[500px] bg-gradient-to-r from-yellow-500/0 via-yellow-500/20 to-yellow-500/0 animate-[spin_10s_linear_infinite]" />
        </div>

        <div className="relative z-10 flex flex-col items-center">
            <motion.div 
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-24 h-24 bg-yellow-500 rounded-full flex items-center justify-center mb-6 shadow-xl"
            >
                <Crown size={48} className="text-black" strokeWidth={2.5} />
            </motion.div>

            <h2 className="text-3xl font-black text-white mb-1 tracking-tight">
                LEVEL UP!
            </h2>
            <p className="text-secondary-foreground/80 text-lg mb-6">איזה אלוף! עלית שלב!</p>

            {/* מעבר רמות */}
            <div className="flex items-center gap-4 bg-muted/50 p-4 rounded-2xl border border-border mb-6">
                <div className="text-muted-foreground font-bold text-xl">{newLevel - 1}</div>
                <ArrowRight className="text-yellow-400" />
                <div className="text-4xl font-black text-yellow-400 drop-shadow-lg">{newLevel}</div>
            </div>

            {/* פרסים */}
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 p-3 rounded-xl w-full flex items-center justify-center gap-3 mb-8">
                <div className="flex items-center gap-1 text-green-400 font-bold">
                    <Coins size={18} /> +100 מטבעות
                </div>
                <div className="w-px h-4 bg-green-500/30"></div>
                <div className="flex items-center gap-1 text-yellow-400 font-bold">
                    <Star size={18} /> בונוס!
                </div>
            </div>

            <button 
                onClick={onClose}
                className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-black py-4 rounded-xl text-lg shadow-lg shadow-yellow-500/20 transition-transform active:scale-95"
            >
                יאללה להמשיך!
            </button>
        </div>
      </motion.div>
    </div>
  );
}